/**
 * 표 구조 연산 (P3-①) — 샘플 block-editor.html의 그리드 알고리즘을 우리 모델에 이식.
 * 모든 함수는 `model`(= {G, columnWidths, ...})을 변형하고 normalize한다. UI 무관(순수).
 *
 *  - insertRow/insertCol: 경계를 가로지르는 병합은 같은 master로 연장.
 *  - deleteRows/deleteCols: 최소 1행/1열 보장.
 *  - mergeCells: 선택 사각형을 좌상단 master로 병합(병합 인지 확장), content 연결.
 *  - splitCell: 병합 해제(1×1 복원).
 *
 * rect 형식: { r1, c1, r2, c2 } (포함 범위).
 */

import { newMaster, normalize } from "./model.js";

function isEmptyContent(content) {
  if (!Array.isArray(content) || !content.length) return true;
  // 공백아닌 텍스트 또는 text가 아닌 노드(link·checkbox·olItem·ulItem 등 인라인 원자)가 있으면 비어있지 않음.
  return !content.some((n) => (n && n.type && n.type !== "text") || ((n && n.text ? n.text : "").trim().length > 0));
}

/** 여러 셀 content를 줄바꿈으로 연결(비어있는 건 제외) */
function concatContents(arrays) {
  const nonEmpty = arrays.filter((a) => !isEmptyContent(a));
  if (!nonEmpty.length) return [];
  const out = [];
  nonEmpty.forEach((a, i) => {
    if (i > 0) out.push({ type: "text", text: "\n", styles: {} });
    for (const n of a) out.push(n);
  });
  return out;
}

function hasRowspan(model) { return model.G.some((row) => row.some((m) => m.rowspan > 1)); }
function hasColspan(model) { return model.G.some((row) => row.some((m) => m.colspan > 1)); }

/** 행 from → to 로 이동(F3). 병합 행이 있으면 미지원(false). */
export function moveRow(model, from, to) {
  const G = model.G;
  if (from === to || from < 0 || to < 0 || from >= G.length || to >= G.length) return false;
  if (hasRowspan(model)) return false;
  const [row] = G.splice(from, 1);
  G.splice(to, 0, row);
  normalize(model);
  return true;
}

/** 열 from → to 로 이동(F3). 병합 열이 있으면 미지원(false). */
export function moveCol(model, from, to) {
  const G = model.G;
  const cols = G[0] ? G[0].length : 0;
  if (from === to || from < 0 || to < 0 || from >= cols || to >= cols) return false;
  if (hasColspan(model)) return false;
  for (const row of G) { const [cell] = row.splice(from, 1); row.splice(to, 0, cell); }
  // 열 순서를 바꾸면 열폭도 함께 이동해야 한다(insertCol/deleteCols와 동일하게 columnWidths 유지).
  if (Array.isArray(model.columnWidths)) { const [w] = model.columnWidths.splice(from, 1); model.columnWidths.splice(to, 0, w); }
  normalize(model);
  return true;
}

/** 병합 셀을 포함하도록 사각형 확장 */
export function expandRect(model, rect) {
  const G = model.G;
  let { r1, c1, r2, c2 } = rect;
  let changed = true;
  while (changed) {
    changed = false;
    for (let r = r1; r <= r2; r++) {
      for (let c = c1; c <= c2; c++) {
        const cell = G[r][c];
        if (!cell) continue;
        if (cell.r < r1) { r1 = cell.r; changed = true; }
        if (cell.c < c1) { c1 = cell.c; changed = true; }
        if (cell.r + cell.rowspan - 1 > r2) { r2 = cell.r + cell.rowspan - 1; changed = true; }
        if (cell.c + cell.colspan - 1 > c2) { c2 = cell.c + cell.colspan - 1; changed = true; }
      }
    }
  }
  return { r1, c1, r2, c2 };
}

export function insertRow(model, at) {
  const G = model.G;
  const C = G[0] ? G[0].length : 0;
  at = Math.max(0, Math.min(at, G.length));
  const row = [];
  for (let c = 0; c < C; c++) {
    if (at > 0 && at < G.length && G[at - 1][c] === G[at][c]) row[c] = G[at - 1][c]; // 세로 병합 연장
    else row[c] = newMaster();
  }
  G.splice(at, 0, row);
  normalize(model);
  return model;
}

export function insertCol(model, at) {
  const G = model.G;
  at = Math.max(0, Math.min(at, G[0] ? G[0].length : 0));
  for (let r = 0; r < G.length; r++) {
    if (at > 0 && at < G[r].length && G[r][at - 1] === G[r][at]) G[r].splice(at, 0, G[r][at - 1]); // 가로 병합 연장
    else G[r].splice(at, 0, newMaster());
  }
  if (Array.isArray(model.columnWidths)) model.columnWidths.splice(at, 0, null);
  normalize(model);
  return model;
}

export function deleteRows(model, r1, r2) {
  const G = model.G;
  if (G.length - (r2 - r1 + 1) < 1) return false;
  G.splice(r1, r2 - r1 + 1);
  normalize(model);
  return true;
}

export function deleteCols(model, c1, c2) {
  const G = model.G;
  if ((G[0] ? G[0].length : 0) - (c2 - c1 + 1) < 1) return false;
  for (let r = 0; r < G.length; r++) G[r].splice(c1, c2 - c1 + 1);
  if (Array.isArray(model.columnWidths)) model.columnWidths.splice(c1, c2 - c1 + 1);
  normalize(model);
  return true;
}

export function mergeCells(model, rect) {
  const G = model.G;
  rect = expandRect(model, rect);
  if (rect.r1 === rect.r2 && rect.c1 === rect.c2) return false;
  const keep = G[rect.r1][rect.c1];
  const seen = new Set([keep]);
  const others = [];
  for (let r = rect.r1; r <= rect.r2; r++) {
    for (let c = rect.c1; c <= rect.c2; c++) {
      const cell = G[r][c];
      if (!seen.has(cell)) {
        seen.add(cell);
        others.push(cell.content);
      }
      G[r][c] = keep;
    }
  }
  const merged = concatContents([keep.content, ...others]);
  keep.content = merged;
  normalize(model);
  return true;
}

export function splitCell(model, master) {
  const G = model.G;
  if (!master || (master.rowspan === 1 && master.colspan === 1)) return false;
  for (let r = master.r; r < master.r + master.rowspan; r++) {
    for (let c = master.c; c < master.c + master.colspan; c++) {
      if (r === master.r && c === master.c) continue;
      G[r][c] = newMaster();
    }
  }
  normalize(model);
  return true;
}
